import { useMemo, useState, memo, useEffect } from 'react';
import classNames from 'classnames';
import { Accordion, AsyncDataHandler, Select } from '@ds';
import { IOption } from '@ds/elements/Select/Select.types';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import { AccordionSize, AccordionVariant, IAccordionTab } from '@ds/elements/Accordion/Accordion.types';
import { useGetDependents } from 'src/shared/queries/useGetDependents';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { useImmigrationDocuments } from 'src/utils/hooks/useImmigrationDocuments';
import { Country, Relationship } from 'src/shared/types/Common';
import { useGetImmigrationSnapshots } from 'src/utils/hooks/useGetImmigrationSnapshots';
import {
  getDropdownLabel,
  getHiddenCountries,
  getUniqueCountries,
  SnapshotCountryCode,
} from './ImmigrationSnapshot.service';
import {
  IImmigrationDocumentGBIE,
  IImmigrationDocumentUS,
  ICountryImmigrationData,
} from 'src/shared/types/Immigration';
import ImmigrationCountryContent from './ImmigrationCountryContent/ImmigrationCountryContent';
import { useThemeStore } from 'src/stores/ThemeStore';
import { ImmigrationInstance } from 'src/utils/constants/common';
import { useGetRemoteBeneficiaryId } from 'src/shared/queries/useGetRemoteBeneficiaryId';
import styles from './ImmigrationSnapshot.module.scss';

const ImmigrationSnapshot = () => {
  const { data: userInfo } = useGetUser();
  const { isTablet, isDesktop } = useScreenSize();
  const { isDeloitteTheme } = useThemeStore();
  const { data: dependents } = useGetDependents(userInfo?.beneId ?? 0);
  const [selectedProfile, setSelectedProfile] = useState<IOption | null>({
    label: userInfo?.fullName || '',
    value: userInfo?.beneId?.toString() ?? '',
  });
  const [activeTabIndex, setActiveTabIndex] = useState<number[]>([0]);
  const [toggleImmigrationSnapshot, setToggleImmigrationSnapshot] = useState(false);
  const beneId = userInfo?.beneId || 0;
  const { data: remoteBeneId } = useGetRemoteBeneficiaryId(beneId ?? 0);

  // Fetch immigration documents for the beneficiary user and dependents
  const {
    beneDocuments,
    dependentsDocuments,
    isFetched: isFetchedImmigrationDocuments,
  } = useImmigrationDocuments(userInfo?.beneId, dependents);

  useEffect(() => {
    if (beneId) {
      setSelectedProfile({ label: userInfo?.fullName || '', value: beneId.toString() });
    }
  }, [beneId]);

  /**
   * Computes a list of unique countries from the beneficiary's documents.
   */
  const flattenedDependentsDocs = useMemo(() => dependentsDocuments.flat(), [dependentsDocuments]);
  const snapshotCountries = useMemo(() => {
    if (!isFetchedImmigrationDocuments) return [];

    const countries = [...(beneDocuments ?? []), ...(flattenedDependentsDocs ?? [])];

    return getUniqueCountries(countries as ICountryImmigrationData[]);
  }, [isFetchedImmigrationDocuments]);

  // Fetch immigration snapshots for the unique selected countries
  const { immigrationSnapshots, isFetched: isFetchedBeneImmigrationSnapshots } = useGetImmigrationSnapshots(
    userInfo?.beneId ?? 0,
    snapshotCountries?.map(({ countryCode }) => countryCode)
  );

  /**
   * Generates a list of selectable profiles (Beneficiary + Dependents).
   * - The Beneficiary is always listed first.
   */
  const profileList = useMemo(() => {
    if (!userInfo || !dependents) return [];

    const beneficiaryFullName =
      userInfo.fullName ??
      `${userInfo.firstName ?? ''} ${userInfo.middleName ?? ''} ${userInfo.lastName ?? ''}`.trim() ??
      ' ';
    const beneficiary = {
      label: getDropdownLabel(beneficiaryFullName, Relationship.Principal),
      value: userInfo.beneId?.toString() ?? '',
    };

    const dependentProfiles = dependents.map((dependent) => {
      const dependentName =
        dependent.fullName ??
        `${dependent?.firstName ?? ''} ${dependent?.middleName ?? ''} ${dependent?.lastName ?? ''}`.trim() ??
        ' ';
      return {
        label: getDropdownLabel(dependentName, dependent.relationship),
        value: dependent.dependentId.toString(),
      };
    });

    return [beneficiary, ...dependentProfiles];
  }, [userInfo, dependents]);

  /**
   * Generates the accordion tabs based on available snapshots.
   * - Filters out countries hidden by business logic.
   */
  const tabs = useMemo(() => {
    if (selectedProfile && snapshotCountries.length > 0 && immigrationSnapshots && beneDocuments) {
      const profileId = Number(selectedProfile.value);
      const countries = getHiddenCountries(snapshotCountries, immigrationSnapshots);
      const isFound = (country: Country, profileId: number) =>
        countries.some(({ countryCode, id }) => countryCode === country.countryCode && id === profileId);
      const cobaltBUSImmigrationDocs = beneDocuments.find(
        (doc) => doc.country === SnapshotCountryCode.UnitedStates && doc.sourceInstance === ImmigrationInstance.BAL
      );
      return snapshotCountries
        .filter(
          (country) => !isFound(country, profileId === beneId && isDeloitteTheme ? (remoteBeneId as number) : profileId)
        )
        .map((country) => {
          const isUS = country.countryCode === SnapshotCountryCode.UnitedStates;
          const snapshots = immigrationSnapshots[country.countryCode];
          let immigrationStatusInfo = null;

          if (profileId === beneId) {
            if (cobaltBUSImmigrationDocs) {
              immigrationStatusInfo = snapshots?.find(
                (snapshot) =>
                  snapshot.sourceInstance === ImmigrationInstance.BAL &&
                  (isDeloitteTheme ? snapshot.beneId === remoteBeneId : snapshot.beneId === profileId)
              );
            }
          } else {
            immigrationStatusInfo = snapshots?.find((item) => item.dependentId === profileId);
          }

          return !immigrationStatusInfo
            ? null
            : {
                header: (
                  <div className={styles['country-header']}>
                    <div className={styles['location-icon']}></div>
                    <p>{country.countryName}</p>
                  </div>
                ),
                body: (
                  <ImmigrationCountryContent
                    isUS={isUS}
                    profileId={profileId}
                    data={
                      isUS
                        ? (immigrationStatusInfo?.immigrationDocumentUS as IImmigrationDocumentUS)
                        : (immigrationStatusInfo?.immigrationDocumentGBIE as IImmigrationDocumentGBIE)
                    }
                  />
                ),
              };
        })
        .filter(Boolean) as IAccordionTab[];
    }
    return null;
  }, [selectedProfile, remoteBeneId, beneId, isDeloitteTheme, snapshotCountries, immigrationSnapshots, beneDocuments]);

  const isLoading = !isFetchedImmigrationDocuments && !isFetchedBeneImmigrationSnapshots;

  return (
    <div
      className={styles['immigration-snapshot-container']}
      onClick={!isDesktop ? () => setToggleImmigrationSnapshot(!toggleImmigrationSnapshot) : undefined}
    >
      <h3
        className={classNames({
          '!mb-0': isTablet && !isLoading && !toggleImmigrationSnapshot,
          [styles['expanded']]: toggleImmigrationSnapshot,
        })}
      >
        Immigration Snapshot
      </h3>
      <AsyncDataHandler isLoading={isLoading}>
        <div
          className={classNames(styles['immigration-snapshot-body'], {
            [styles['show-immigration-snapshot-body']]: toggleImmigrationSnapshot,
          })}
          onClick={(event) => event.stopPropagation()}
        >
          {profileList.length > 0 && (
            <Select
              options={profileList}
              selectedOption={selectedProfile}
              onChange={setSelectedProfile}
              placeholder="Select Profile"
              className={styles['profiles-dropdown']}
            ></Select>
          )}
          {!isLoading && tabs && tabs.length > 0 && (
            <div className={styles['snapshot-countries']}>
              <Accordion
                className={styles['snapshot-country-accordion']}
                tabs={tabs}
                variant={AccordionVariant.Secondary}
                size={AccordionSize.Large}
                highlightActive={false}
                multiple={true}
                preventTabsToggle={tabs.length === 1}
                activeIndex={activeTabIndex}
                onTabChange={(event) => setActiveTabIndex(event.index as number[])}
              />
            </div>
          )}
          {!isLoading && (tabs?.length === 0 || snapshotCountries.length == 0) && (
            <div className={styles['no-snapshot-section']}>
              <div
                className={classNames(styles['no-snapshot-icon'], {
                  [styles['no-snapshot-deloitte-icon']]: isDeloitteTheme,
                })}
              ></div>
              <span>No data to show here yet.</span>
            </div>
          )}
        </div>
      </AsyncDataHandler>
    </div>
  );
};

export default memo(ImmigrationSnapshot);
